/* Stable drawer regions and a single FIFO for theme cart writes. */
(() => {
  'use strict';
  let tail = Promise.resolve();
  let pending = 0;
  let checkoutClean = false;
  const helper = () => window.Alpine?.store('xCartHelper');
  const regions = ['.mw-cart__title-wrap', '.mw-cart__notices', '#CartDrawer-CartItems', '.mw-cart__footer-scroll'];

  function replaceContent(target, source) {
    // Let Alpine's own MutationObserver clean up removed components and mount
    // their replacements. This theme's Alpine build has no public destroyTree.
    target.innerHTML = source.innerHTML;
  }

  function renderDrawer(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
    const current = document.getElementById('CartDrawer');
    if (!fresh || !current) throw new Error('Cart drawer response is incomplete.');
    const active = document.activeElement;
    const activeId = current.contains(active) ? active.id : '';
    const scroll = current.querySelector('.mw-cart__scroll-column');
    const scrollTop = scroll?.scrollTop || 0;
    const stable = current.querySelector('#CartDrawer-Form') && fresh.querySelector('#CartDrawer-Form');
    if (stable) {
      for (const selector of regions) {
        const target = current.querySelector(selector), source = fresh.querySelector(selector);
        if (target && source) replaceContent(target, source);
      }
      window.mwCartUpsells?.reconcile(fresh);
      // Cart-dependent discount summaries also appear inside the discount sheet.
      current.querySelectorAll('.mw-cart__applied').forEach((target, index) => {
        const source = fresh.querySelectorAll('.mw-cart__applied')[index];
        if (source) replaceContent(target, source);
      });
    } else {
      window.mwCartUpsells?.dispose();
      document.dispatchEvent(new CustomEvent('mw:cart:reset'));
      replaceContent(current, fresh);
    }
    current.dataset.itemCount = fresh.dataset.itemCount;
    const newScroll = current.querySelector('.mw-cart__scroll-column');
    if (newScroll) newScroll.scrollTop = scrollTop;
    if (activeId && !active.isConnected) (document.getElementById(activeId) || current.querySelector('#CloseCart'))?.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('mw:cart:rendered'));
  }

  async function acquire() {
    pending++;
    checkoutClean = false;
    const previous = tail;
    let finish;
    tail = new Promise(resolve => { finish = resolve; });
    await previous;
    // Older integrations participate through the same busy flags.
    while (window.updatingCart || window.updatingEstimate) await new Promise(requestAnimationFrame);
    window.updatingCart = true;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      window.updatingCart = false;
      pending--;
      finish();
    };
  }
  async function run(task) {
    const release = await acquire();
    try { return await task(); } finally { release(); }
  }

  // Liquid keys can change when an app updates line properties without refreshing
  // the drawer. Resolve the shopper's current row against fresh cart JSON inside
  // the write queue, then use that response's position for the change request.
  async function resolveLine(itemKey) {
    const drawerRows = [...document.querySelectorAll('#CartDrawer [data-mw-item-key]')];
    const rows = drawerRows.length ? drawerRows : [...document.querySelectorAll('#main-cart-items .cart-item')];
    const row = rows.find(element => element.dataset.mwItemKey === itemKey || element.querySelector?.(`[id="cart-qty-${itemKey}"]`));
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart could not be loaded (${response.status}).`);
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Cart response is incomplete.');
    const exact = cart.items.findIndex(item => item.key === itemKey);
    if (exact >= 0) return exact + 1;
    if (!row || !row.isConnected) throw new Error('This cart item changed. Please try again.');
    const position = rows.indexOf(row);
    const item = cart.items[position];
    const variant = Number(String(itemKey).split(':')[0]);
    if (!item || Number(item.variant_id) !== variant) throw new Error('This cart item changed. Please try again.');
    let renderedProperties;
    try { renderedProperties = JSON.parse(row.getAttribute('x-data-properties') || '{}'); }
    catch (_) { renderedProperties = {}; }
    // Shipping estimates are intentionally dynamic; private bundle properties
    // identify separate lines of the same variant without depending on that text.
    for (const [key, value] of Object.entries(renderedProperties)) {
      if (!key.startsWith('_') || String(item.properties?.[key] ?? '') === String(value)) continue;
      throw new Error('This cart item changed. Please try again.');
    }
    return position + 1;
  }

  window.mwCart = { renderDrawer, replaceContent, acquire, run, resolveLine, get pending() { return pending; }, settled: () => tail };

  let preparing = false;
  let clicked = false;
  function block(event) { event.preventDefault(); event.stopImmediatePropagation(); }
  async function prepare(event, button) {
    const cart = helper();
    if (!cart || !button) return;
    if (preparing) { block(event); return; }
    cart.validateCart(true);
    if (!cart.validated) { block(event); return; }
    const saves = [];
    document.dispatchEvent(new CustomEvent('mw:cart:flush', { detail: { saves } }));
    if (!checkoutClean) saves.push(cart.updateCart({ attributes: {
      'collection-pagination': null, 'blog-pagination': null, 'choose_option_id': null, 'datetime-updated': null
    } }));
    if (!saves.length && !pending && !window.updatingCart && !window.updatingEstimate) return;
    block(event);
    preparing = true;
    try {
      await Promise.all(saves);
      await tail;
      await cart.waitForCartUpdate();
      checkoutClean = true;
      preparing = false;
      if (button.isConnected) button.click();
    } catch (error) {
      const errorBox = document.querySelector('#CartDrawer-CartErrors, #cart-errors');
      if (errorBox) errorBox.textContent = error.message || 'Could not save cart details. Please try again.';
    } finally { preparing = false; }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"]');
    if (!button) return;
    prepare(event, button);
    clicked = !event.defaultPrevented;
    setTimeout(() => { clicked = false; }, 0);
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches?.('#CartDrawer-Form, #cart')) return;
    const button = event.submitter?.name === 'checkout' ? event.submitter : document.querySelector(`button[name="checkout"][form="${event.target.id}"]`);
    if (window.AgeChecker_APP_URL && !clicked && button) { block(event); button.click(); return; }
    prepare(event, button);
  }, true);
})();
